/**
 * Utilidades para procesar datos de traces y generar métricas para el dashboard
 */
import type { LangfuseTrace } from '@/types/traces';

export interface DashboardMetrics {
  totalCalls: number;
  callsToday: number;
  averageDuration: number;
  successRate: number;
  uniqueUsers: number;
  uniqueSessions: number;
  totalCost?: number;
}

export interface DailyVolumeData {
  date: string;
  calls: number;
  avgDuration: number;
  successRate: number;
}

export interface HourlyDistribution {
  hour: string;
  calls: number;
  avgDuration: number;
}

export interface EnvironmentStats {
  name: string;
  value: number;
  percentage: number;
  color: string;
}

export interface UserActivity {
  userId: string;
  callCount: number;
  avgDuration: number;
  lastActivity: string;
}

export interface SessionAnalysis {
  sessionId: string;
  callCount: number;
  totalDuration: number;
  firstCall: string;
  lastCall: string;
  success: boolean;
}

/**
 * Procesa los traces para calcular métricas del dashboard
 */
export const processDashboardMetrics = (traces: LangfuseTrace[]): DashboardMetrics => {
  if (!traces || traces.length === 0) {
    return {
      totalCalls: 0,
      callsToday: 0,
      averageDuration: 0,
      successRate: 0,
      uniqueUsers: 0,
      uniqueSessions: 0,
      totalCost: 0
    };
  }

  const today = new Date().toDateString();
  const callsToday = traces.filter(trace => 
    new Date(trace.timestamp).toDateString() === today
  ).length;

  const successfulCalls = traces.filter(trace => trace.success !== false).length;
  const successRate = traces.length > 0 ? (successfulCalls / traces.length) * 100 : 0;

  const averageDuration = traces.length > 0 
    ? traces.reduce((sum, trace) => sum + (trace.latency || 0), 0) / traces.length
    : 0;

  const uniqueUsers = new Set(traces.map(trace => trace.userId).filter(Boolean)).size;
  const uniqueSessions = new Set(traces.map(trace => trace.sessionId).filter(Boolean)).size;
  const totalCost = traces.reduce((sum, trace) => sum + (trace.totalCost || 0), 0);

  return {
    totalCalls: traces.length,
    callsToday,
    averageDuration,
    successRate,
    uniqueUsers,
    uniqueSessions,
    totalCost
  };
};

/**
 * Procesa datos para gráfica de volumen diario
 */
export const processDailyVolume = (traces: LangfuseTrace[], days: number = 30): DailyVolumeData[] => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - days + 1);

  const dailyData: Record<string, { calls: number; durations: number[]; successes: number }> = {};

  // Inicializar todos los días
  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    const dateStr = d.toISOString().split('T')[0];
    dailyData[dateStr] = { calls: 0, durations: [], successes: 0 };
  }

  // Procesar traces
  traces.forEach(trace => {
    const dateStr = new Date(trace.timestamp).toISOString().split('T')[0];
    if (dailyData[dateStr]) {
      dailyData[dateStr].calls++;
      if (trace.latency) {
        dailyData[dateStr].durations.push(trace.latency);
      }
      if (trace.success !== false) {
        dailyData[dateStr].successes++;
      }
    }
  });

  return Object.entries(dailyData)
    .map(([date, data]) => ({
      date,
      calls: data.calls,
      avgDuration: data.durations.length > 0 
        ? data.durations.reduce((sum, d) => sum + d, 0) / data.durations.length 
        : 0,
      successRate: data.calls > 0 ? (data.successes / data.calls) * 100 : 100
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
};

/**
 * Procesa distribución por horas del día
 */
export const processHourlyDistribution = (traces: LangfuseTrace[]): HourlyDistribution[] => {
  const hourlyData: Record<number, { calls: number; durations: number[] }> = {};

  // Inicializar todas las horas
  for (let hour = 0; hour < 24; hour++) {
    hourlyData[hour] = { calls: 0, durations: [] };
  }

  traces.forEach(trace => {
    const hour = new Date(trace.timestamp).getHours();
    hourlyData[hour].calls++;
    if (trace.latency) {
      hourlyData[hour].durations.push(trace.latency);
    }
  });

  return Array.from({ length: 24 }, (_, hour) => ({
    hour: `${hour.toString().padStart(2, '0')}:00`,
    calls: hourlyData[hour].calls,
    avgDuration: hourlyData[hour].durations.length > 0
      ? hourlyData[hour].durations.reduce((sum, d) => sum + d, 0) / hourlyData[hour].durations.length
      : 0
  }));
};

/**
 * Procesa estadísticas por ambiente
 */
export const processEnvironmentStats = (traces: LangfuseTrace[]): EnvironmentStats[] => {
  const envCounts: Record<string, number> = {};
  const total = traces.length;

  traces.forEach(trace => {
    const env = trace.environment || 'unknown';
    envCounts[env] = (envCounts[env] || 0) + 1;
  });

  const colors = {
    production: '#22c55e',
    development: '#eab308',
    staging: '#3b82f6',
    unknown: '#6b7280'
  };

  return Object.entries(envCounts)
    .map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value,
      percentage: total > 0 ? (value / total) * 100 : 0,
      color: colors[name as keyof typeof colors] || '#6b7280'
    }))
    .sort((a, b) => b.value - a.value);
};

/**
 * Procesa tasa de éxito global
 */
export const processSuccessRate = (traces: LangfuseTrace[]): EnvironmentStats[] => {
  const successCount = traces.filter(trace => trace.success !== false).length;
  const failCount = traces.length - successCount;

  return [
    {
      name: 'Exitosas',
      value: successCount,
      percentage: traces.length > 0 ? (successCount / traces.length) * 100 : 0,
      color: '#22c55e'
    },
    {
      name: 'Fallidas',
      value: failCount,
      percentage: traces.length > 0 ? (failCount / traces.length) * 100 : 0,
      color: '#ef4444'
    }
  ].filter(item => item.value > 0);
};

/**
 * Procesa top usuarios más activos
 */
export const processTopUsers = (traces: LangfuseTrace[], limit: number = 5): UserActivity[] => {
  const userStats: Record<string, { count: number; durations: number[]; lastActivity: string }> = {};

  traces.forEach(trace => {
    if (trace.userId) {
      if (!userStats[trace.userId]) {
        userStats[trace.userId] = { count: 0, durations: [], lastActivity: trace.timestamp };
      }
      userStats[trace.userId].count++;
      if (trace.latency) {
        userStats[trace.userId].durations.push(trace.latency);
      }
      if (new Date(trace.timestamp) > new Date(userStats[trace.userId].lastActivity)) {
        userStats[trace.userId].lastActivity = trace.timestamp;
      }
    }
  });

  return Object.entries(userStats)
    .map(([userId, stats]) => ({
      userId: userId.length > 10 ? `${userId.slice(0, 8)}...` : userId,
      callCount: stats.count,
      avgDuration: stats.durations.length > 0 
        ? stats.durations.reduce((sum, d) => sum + d, 0) / stats.durations.length 
        : 0,
      lastActivity: stats.lastActivity
    }))
    .sort((a, b) => b.callCount - a.callCount)
    .slice(0, limit);
};

/**
 * Procesa análisis de sesiones
 */
export const processSessionAnalysis = (traces: LangfuseTrace[]): SessionAnalysis[] => {
  const sessionStats: Record<string, { traces: LangfuseTrace[] }> = {};

  traces.forEach(trace => {
    if (trace.sessionId) {
      if (!sessionStats[trace.sessionId]) {
        sessionStats[trace.sessionId] = { traces: [] };
      }
      sessionStats[trace.sessionId].traces.push(trace);
    }
  });

  return Object.entries(sessionStats)
    .map(([sessionId, data]) => {
      const sessionTraces = data.traces.sort((a, b) => 
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      
      const totalDuration = sessionTraces.reduce((sum, trace) => sum + (trace.latency || 0), 0);
      const allSuccess = sessionTraces.every(trace => trace.success !== false);

      return {
        sessionId: sessionId.length > 10 ? `${sessionId.slice(0, 8)}...` : sessionId,
        callCount: sessionTraces.length,
        totalDuration,
        firstCall: sessionTraces[0]?.timestamp || '',
        lastCall: sessionTraces[sessionTraces.length - 1]?.timestamp || '',
        success: allSuccess
      };
    })
    .sort((a, b) => b.callCount - a.callCount);
};

/**
 * Genera datos para heatmap de actividad (día de la semana vs hora)
 */
export const processActivityHeatmap = (traces: LangfuseTrace[]) => {
  const heatmapData: { x: number; y: number; value: number; day: string; hour: string }[] = [];
  const days = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  
  // Inicializar matriz 7x24
  const matrix: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
  
  traces.forEach(trace => {
    const date = new Date(trace.timestamp);
    const dayOfWeek = date.getDay(); // 0 = domingo
    const hour = date.getHours();
    matrix[dayOfWeek][hour]++;
  });

  // Convertir matriz a datos para heatmap
  for (let day = 0; day < 7; day++) {
    for (let hour = 0; hour < 24; hour++) {
      heatmapData.push({
        x: day,
        y: hour,
        value: matrix[day][hour],
        day: days[day],
        hour: `${hour.toString().padStart(2, '0')}:00`
      });
    }
  }

  return heatmapData;
};

/**
 * Calcula tendencias y comparaciones de períodos
 */
export const calculateTrends = (traces: LangfuseTrace[], days: number = 30) => {
  const currentPeriod = traces.filter(trace => {
    const traceDate = new Date(trace.timestamp);
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    return traceDate >= cutoff;
  });

  const previousPeriod = traces.filter(trace => {
    const traceDate = new Date(trace.timestamp);
    const startCutoff = new Date();
    startCutoff.setDate(startCutoff.getDate() - (days * 2));
    const endCutoff = new Date();
    endCutoff.setDate(endCutoff.getDate() - days);
    return traceDate >= startCutoff && traceDate < endCutoff;
  });

  const currentMetrics = processDashboardMetrics(currentPeriod);
  const previousMetrics = processDashboardMetrics(previousPeriod);

  const calculateChange = (current: number, previous: number): { value: number; percentage: number; trend: 'up' | 'down' | 'stable' } => {
    const diff = current - previous;
    const percentage = previous > 0 ? (diff / previous) * 100 : 0;
    
    return {
      value: diff,
      percentage,
      trend: Math.abs(percentage) < 5 ? 'stable' : percentage > 0 ? 'up' : 'down'
    };
  };

  return {
    calls: calculateChange(currentMetrics.totalCalls, previousMetrics.totalCalls),
    avgDuration: calculateChange(currentMetrics.averageDuration, previousMetrics.averageDuration),
    successRate: calculateChange(currentMetrics.successRate, previousMetrics.successRate),
    uniqueUsers: calculateChange(currentMetrics.uniqueUsers, previousMetrics.uniqueUsers),
  };
};